import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { IntegrationConnectionResultMapper } from '../../mappers/IntegrationConnectionResultMapper';
import { IntegrationConnectionResult } from '../../models/IntegrationConnectionResult';
import { ServiceNowMapper } from '../mappers/ServiceNowMapper';
import { ServiceNowIntegration } from '../models/ServiceNowIntegration';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { OrganizationIntegration } from '../../../common/enums/OrganizationIntegration';
import { BatchExportMapper } from '../../exports/mappers/BatchExportMapper';

export const GET_SERVICE_NOW_INTEGRATION = `
query {
  viewer {
    organization {
      integrations {
        ... on ServiceNowIntegration {
          instanceUrl
          userID
        }
      }
    }
  }
}
`;

export const UPDATE_SERVICE_NOW_SETTINGS = `
mutation ($instanceUrl: String!, $userId: String!, $password: String!) {
  updateServiceNowIntegration(
    input: {instanceUrl: $instanceUrl, userID: $userId, password: $password}
  ) {
    serviceNowIntegration {
      instanceUrl
      userID
    }
  }
}
`;

export const CHECK_SERVICE_NOW_SETTINGS = `
mutation ($instanceUrl: String!, $userId: String!, $password: String) {
  checkServiceNowIntegration(
    input: {instanceUrl: $instanceUrl, userID: $userId, password: $password}
  ) {
    connectionResult {
      isSuccessful
      reason
    }
  }
}`;

export const UNLINK_SERVICE_NOW_SETTINGS = `
mutation {
  unlinkServiceNowIntegration(input: {}) {
    __typename
  }
}`;

export const EXPORT_INSTANCE = `
mutation ($instanceId: ID!) {
  serviceNowExportInstance(input: {instanceId: $instanceId}) {
    incident{
      instanceId
      number
      url
    }
  }
}`;

export const SERVICE_NOW_EXPORT_REPORT = `
mutation ($reportId: ID!, $ruleNumbers: [Int!], $instanceIds: [ID!], $keptByFilters: Boolean) {
  serviceNowExportReport(
    input: {reportId: $reportId, ruleNumbers: $ruleNumbers, instanceIds: $instanceIds, instanceIsKeptByFilters: $keptByFilters}
  ) {
    export {
      __typename
      exportType
      status
      totalCount
      exportedCount
      createdAt
      canceledAt
    }
  }
}`;

export const CANCEL_EXPORT = `
mutation ($reportId: ID!) {
  serviceNowCancelReportExport(input: {reportId: $reportId}) {
    export {
      __typename
      exportType
      status
      totalCount
      exportedCount
      createdAt
      canceledAt
    }
  }
}`;

export type UpdateServiceNowArgs = {
  instanceUrl: string;
  userId: string;
  password: string;
};

export type CheckServiceNowConnectionArgs = {
  instanceUrl: string;
  userId: string;
  password?: string;
};

export class ServiceNowRepository extends BaseGQLService {
  private readonly testConnectionResultMapper = new IntegrationConnectionResultMapper();
  private readonly mapper = new ServiceNowMapper();
  private readonly exportMapper = new BatchExportMapper();

  async getIntegration() {
    return this.query(GET_SERVICE_NOW_INTEGRATION, {}, (data) => {
      for (const integration of data.viewer.organization.integrations) {
        if (integration.__typename === OrganizationIntegration.SERVICE_NOW) {
          return this.mapper.mapGQLModel(integration);
        }
      }
      return null;
    });
  }

  async checkConnection(args: CheckServiceNowConnectionArgs): Promise<IntegrationConnectionResult> {
    return this.mutate(
      CHECK_SERVICE_NOW_SETTINGS,
      {
        instanceUrl: args.instanceUrl,
        userId: args.userId,
        password: args.password || null,
      },
      (data) => this.testConnectionResultMapper.mapGQLModel(data.checkServiceNowIntegration.connectionResult)
    );
  }

  async updateIntegration(args: UpdateServiceNowArgs): Promise<ServiceNowIntegration> {
    return this.mutate(
      UPDATE_SERVICE_NOW_SETTINGS,
      {
        instanceUrl: args.instanceUrl,
        userId: args.userId,
        password: args.password,
      },
      (data) => {
        return this.mapper.mapGQLModel(data.updateServiceNowIntegration.serviceNowIntegration);
      }
    );
  }

  async unlinkIntegration(): Promise<void> {
    return this.mutate(UNLINK_SERVICE_NOW_SETTINGS, {});
  }

  async batchExport(reportId: number, ruleNumbers: number[] = [], instanceIds: number[] = [], keptByFilters: boolean | null = null) {
    return this.mutate(
      SERVICE_NOW_EXPORT_REPORT,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ruleNumbers,
        instanceIds: instanceIds.map((instanceId) => this.gqlIdEncoder.encode(ModelType.Instance, instanceId)),
        keptByFilters: keptByFilters ? null : true,
      },
      (data) => {
        return this.exportMapper.mapExport(data.serviceNowExportReport.export);
      }
    );
  }

  async cancelBatchExport(reportId: number) {
    return this.mutate(
      CANCEL_EXPORT,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
      },
      (data) => {
        return this.exportMapper.mapExport(data.serviceNowCancelReportExport.export);
      }
    );
  }

  async exportInstance(instanceId: number) {
    return this.mutate(
      EXPORT_INSTANCE,
      {
        instanceId: this.gqlIdEncoder.encode(ModelType.Instance, instanceId),
      },
      (data) => {
        return this.mapper.mapExportedInstance(data.serviceNowExportInstance.incident);
      }
    );
  }
}
